<template>
  <v-container
    id="notifications"
    fluid
    tag="section"
    class="np-page"
  >
    <v-row justify="center">
      <v-col cols="12" md="8">
        <!-- Header -->
        <div class="d-flex align-center mb-6">
          <div class="np-header-icon mr-3">
            <v-icon color="#22d3ee">mdi-bell-outline</v-icon>
          </div>
          <div>
            <div class="np-title">Notificações</div>
            <div class="np-subtitle">
              {{ unreadCount }} não lida{{ unreadCount === 1 ? '' : 's' }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            depressed
            class="np-markall-btn"
            :disabled="unreadCount === 0"
            @click="markAllAsRead"
          >
            <v-icon left small color="#4ade80">mdi-check-all</v-icon>
            Marcar todas como lidas
          </v-btn>
        </div>

        <!-- Filtros -->
        <div class="d-flex align-center mb-5" style="gap: 8px">
          <button
            class="np-filter"
            :class="{ active: filter === 'all' }"
            @click="filter = 'all'"
          >
            Todas
            <span class="np-filter-count">{{ notifications.length }}</span>
          </button>
          <button
            class="np-filter"
            :class="{ active: filter === 'unread' }"
            @click="filter = 'unread'"
          >
            Não lidas
            <span class="np-filter-count">{{ unreadCount }}</span>
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="np-state text-center">
          <v-progress-circular indeterminate color="#22d3ee" size="48" />
          <div class="np-state-text mt-3">Carregando notificações...</div>
        </div>

        <template v-else>
          <!-- Lista -->
          <div
            v-for="notification in filteredNotifications"
            :key="notification.id"
            class="np-card"
            :class="{ unread: !notification.read }"
            @click="handleNotificationClick(notification)"
          >
            <div
              class="np-icon-box mr-4"
              :style="{ backgroundColor: getNotificationColor(notification.displayType) + '22' }"
            >
              <v-icon size="20" :color="getNotificationColor(notification.displayType)">
                {{ getNotificationIcon(notification.displayType) }}
              </v-icon>
            </div>

            <div class="np-body">
              <div class="d-flex align-center">
                <span class="np-card-title">{{ notification.title }}</span>
                <span v-if="!notification.read" class="np-dot ml-2"></span>
              </div>
              <div class="np-card-msg">
                {{ notification.message || notification.title }}
              </div>
              <div class="np-card-time">{{ notification.timeAgo }}</div>
            </div>

            <div class="np-actions">
              <button
                v-if="!notification.read"
                class="np-mark-link"
                @click.stop="markAsRead(notification)"
              >
                <v-icon size="15" color="#22d3ee">mdi-check</v-icon>
                Marcar como lida
              </button>
              <v-btn
                icon
                small
                class="np-delete"
                @click.stop="confirmDelete(notification)"
              >
                <v-icon size="18">mdi-trash-can-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Estado Vazio -->
          <div v-if="filteredNotifications.length === 0" class="np-state text-center">
            <v-icon size="56" color="grey">mdi-bell-off-outline</v-icon>
            <div class="np-state-title mt-3">Nenhuma notificação</div>
            <div class="np-state-text">
              {{ filter === 'unread' ? 'Você não tem notificações não lidas.' : 'Você está em dia! Não há notificações no momento.' }}
            </div>
          </div>
        </template>
      </v-col>
    </v-row>

    <!-- Dialog de Confirmação -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">
          {{ deleteMode === 'single' ? 'Deletar Notificação?' : 'Limpar Todas as Notificações?' }}
        </v-card-title>
        <v-card-text>
          <p v-if="deleteMode === 'single'" class="mb-0">
            Tem certeza que deseja deletar esta notificação? Esta ação não pode ser desfeita.
          </p>
          <p v-else class="mb-0">
            Tem certeza que deseja deletar <strong>todas as {{ notifications.length }} notificações</strong>? Esta ação não pode ser desfeita.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn
            color="error"
            text
            @click="executeDelete"
            :loading="deleting"
          >
            Deletar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { NotificationsService } from '@/services'

export default {
  name: 'Notifications',

  data: () => ({
    loading: false,
    notifications: [],
    filter: 'all', // 'all' | 'unread'

    // Delete
    deleteDialog: false,
    deleteMode: 'single', // 'single' ou 'all'
    notificationToDelete: null,
    deleting: false,

    // Snackbar
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.read).length
    },

    filteredNotifications() {
      if (this.filter === 'unread') {
        return this.notifications.filter(n => !n.read)
      }
      return this.notifications
    },
  },

  mounted() {
    this.loadNotifications()
  },

  methods: {
    async loadNotifications() {
      this.loading = true
      try {
        const response = await NotificationsService.list()

        // Mapear dados do backend e ordenar por data (mais recentes primeiro)
        this.notifications = (response.data || [])
          .map(notification => {
            return {
              ...notification,
              // Preservar tipo original para redirect, criar displayType para cores/ícones
              displayType: this.getNotificationTypeFromBackend(notification.type, notification.title),
              timeAgo: this.getTimeAgo(notification.createdAt),
            }
          })
          .sort((a, b) => {
            // Ordenar por createdAt decrescente (mais recente primeiro)
            const dateA = new Date(a.createdAt)
            const dateB = new Date(b.createdAt)
            return dateB - dateA
          })
      } catch (error) {
        console.error('Erro ao carregar notificações:', error)
        this.showSnackbar('Erro ao carregar notificações', 'error')
      } finally {
        this.loading = false
      }
    },

    getNotificationTypeFromBackend(backendType, title) {
      // Mapear tipos do backend para tipos do frontend
      const typeMap = {
        'ONE_ON_ONE': 'meeting',
        'PDI': 'pdi',
        'FEEDBACK': 'feedback',
        'ACHIEVEMENT': 'achievement',
        'CAMPAIGN': 'campaign',
        'APPROVAL': 'approval',
        'APPROVAL_NEW': 'approval',
        'APPROVAL_COMPLETION': 'approval',
      }

      if (backendType && typeMap[backendType]) {
        return typeMap[backendType]
      }

      // Fallback: inferir pelo título
      return this.inferNotificationType(title || '')
    },

    inferNotificationType(title) {
      // Inferir tipo baseado no título como fallback
      const t = title.toLowerCase()
      if (t.includes('feedback')) return 'feedback'
      if (t.includes('pdi') || t.includes('certificação')) return 'pdi'
      if (t.includes('reunião') || t.includes('1:1')) return 'meeting'
      if (t.includes('conquista') || t.includes('badge')) return 'achievement'
      if (t.includes('aprovação') || t.includes('aprovar') || t.includes('pendente')) return 'approval'
      if (t.includes('campanha')) return 'campaign'
      if (t.includes('relatório') || t.includes('relatorio')) return 'report'
      if (t.includes('bem-vindo') || t.includes('boas-vindas') || t.includes('boas vindas')) return 'welcome'
      return 'general'
    },

    getTimeAgo(createdAt) {
      if (!createdAt) return 'Agora'

      const now = new Date()
      const date = new Date(createdAt)
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Agora'
      if (diffMins < 60) return `há ${diffMins}min`
      if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`
      if (diffDays === 1) return 'ontem'
      if (diffDays < 7) return `há ${diffDays} dias`
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7)
        return `há ${weeks} semana${weeks > 1 ? 's' : ''}`
      }
      return date.toLocaleDateString('pt-BR')
    },

    getNotificationColor(type) {
      const normalizedType = (type || '').toLowerCase().replace(/_/g, '')
      const colors = {
        meeting: '#22d3ee',
        oneonone: '#22d3ee',
        feedback: '#38bdf8',
        pdi: '#a855f7',
        achievement: '#f59e0b',
        approval: '#fb923c',
        campaign: '#fb923c',
        report: '#4ade80',
        welcome: '#9ca3af',
        general: '#9ca3af',
      }
      return colors[normalizedType] || '#9ca3af'
    },

    getNotificationIcon(type) {
      const normalizedType = (type || '').toLowerCase().replace(/_/g, '')
      const icons = {
        meeting: 'mdi-calendar-outline',
        oneonone: 'mdi-calendar-outline',
        feedback: 'mdi-message-text-outline',
        pdi: 'mdi-bullseye-arrow',
        achievement: 'mdi-trophy-outline',
        approval: 'mdi-clipboard-check-outline',
        campaign: 'mdi-bullhorn-outline',
        report: 'mdi-poll',
        welcome: 'mdi-information-outline',
        general: 'mdi-information-outline',
      }
      return icons[normalizedType] || 'mdi-bell-outline'
    },

    async handleNotificationClick(notification) {
      console.log('🔔 Clique na notificação:', notification)

      // Redireciona baseado no tipo
      const typeRouteMap = {
        'FEEDBACK': '/feedbacks',
        'PDI': '/pdis',
        'ONE_ON_ONE': '/one-on-ones',
        'MEETING': '/one-on-ones',
        'APPROVAL': '/approval',
        'APPROVAL_NEW': '/approval?tab=0&status=PENDING_APPROVAL',
        'APPROVAL_COMPLETION': '/approval?tab=0&status=PENDING_COMPLETION',
        'CAMPAIGN': '/campaigns',
      }

      // Normalizar tipo para uppercase (backend envia ONE_ON_ONE)
      const notificationType = (notification.type || '').toUpperCase()
      const route = typeRouteMap[notificationType]

      if (route) {
        // Marca como lida antes de redirecionar (não bloqueante)
        if (!notification.read) {
          NotificationsService.markAsRead(notification.id).catch(error => {
            console.error('Erro ao marcar notificação:', error)
          })
        }

        // Redireciona imediatamente
        this.$router.push(route).catch(err => {
          console.error('Erro ao navegar:', err)
        })
      } else {
        // Se não houver rota, só marca como lida
        if (!notification.read) {
          try {
            await NotificationsService.markAsRead(notification.id)
            notification.read = true
            this.showSnackbar('Notificação marcada como lida', 'success')
          } catch (error) {
            console.error('Erro ao marcar notificação:', error)
          }
        }
      }
    },

    async markAsRead(notification) {
      try {
        await NotificationsService.markAsRead(notification.id)
        notification.read = true
        this.showSnackbar('Notificação marcada como lida', 'success')
      } catch (error) {
        console.error('Erro ao marcar notificação:', error)
        this.showSnackbar('Erro ao marcar notificação', 'error')
      }
    },

    async markAllAsRead() {
      try {
        const unreadIds = this.notifications.filter(n => !n.read).map(n => n.id)
        if (unreadIds.length === 0) {
          this.showSnackbar('Não há notificações não lidas', 'info')
          return
        }

        await NotificationsService.markAllAsRead(unreadIds)
        this.notifications.forEach(n => { n.read = true })
        this.showSnackbar('Todas as notificações foram marcadas como lidas', 'success')
      } catch (error) {
        console.error('Erro ao marcar todas:', error)
        this.showSnackbar('Erro ao marcar todas como lidas', 'error')
      }
    },

    confirmDelete(notification) {
      this.deleteMode = 'single'
      this.notificationToDelete = notification
      this.deleteDialog = true
    },

    confirmClearAll() {
      this.deleteMode = 'all'
      this.notificationToDelete = null
      this.deleteDialog = true
    },

    async executeDelete() {
      this.deleting = true
      try {
        if (this.deleteMode === 'single') {
          // Deletar notificação individual
          await NotificationsService.delete(this.notificationToDelete.id)
          const index = this.notifications.findIndex(n => n.id === this.notificationToDelete.id)
          if (index > -1) {
            this.notifications.splice(index, 1)
          }
          this.showSnackbar('Notificação deletada com sucesso', 'success')
        } else {
          // Deletar todas as notificações
          const allIds = this.notifications.map(n => n.id)
          await NotificationsService.deleteBatch(allIds)
          this.notifications = []
          this.showSnackbar(`${allIds.length} notificações deletadas com sucesso`, 'success')
        }

        this.deleteDialog = false
        this.notificationToDelete = null
      } catch (error) {
        console.error('Erro ao deletar:', error)
        const message = this.deleteMode === 'single'
          ? 'Erro ao deletar notificação'
          : 'Erro ao deletar notificações'
        this.showSnackbar(message, 'error')
      } finally {
        this.deleting = false
      }
    },

    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
}
</script>

<style scoped>
.np-page {
  min-height: 100vh;
}

/* ─── Header ─────────────────────────────────────────── */
.np-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(34, 211, 238, 0.12);
  flex-shrink: 0;
}

.np-title {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.np-subtitle {
  font-size: 13px;
  margin-top: 2px;
}

.np-markall-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  border-radius: 8px !important;
  height: 40px !important;
}

/* ─── Filtros ────────────────────────────────────────── */
.np-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.15s ease;
}

.np-filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 700;
}

/* ─── Card ───────────────────────────────────────────── */
.np-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 18px 20px;
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.np-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.np-body {
  min-width: 0;
  flex: 1;
}

.np-card-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.np-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #22d3ee;
  flex-shrink: 0;
}

.np-card-msg {
  font-size: 13.5px;
  line-height: 1.4;
  margin-top: 4px;
  color: #888888;
}

.np-card-time {
  font-size: 12px;
  margin-top: 8px;
  color: #6b6b6b;
}

/* ─── Ações do card ──────────────────────────────────── */
.np-actions {
  position: absolute;
  top: 16px;
  right: 18px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.np-mark-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 600;
  color: #22d3ee;
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.np-mark-link:hover {
  background-color: rgba(34, 211, 238, 0.1);
}

.np-delete {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.np-card:hover .np-delete {
  opacity: 1;
}

/* ─── Estados (loading/vazio) ────────────────────────── */
.np-state {
  padding: 56px 16px;
}

.np-state-title {
  font-size: 16px;
  font-weight: 600;
}

.np-state-text {
  font-size: 13px;
  color: #888888;
}

/* ════════════════════════════════════════════════════════
   DARK
   ════════════════════════════════════════════════════════ */
.theme--dark .np-title,
.theme--dark .np-card-title,
.theme--dark .np-state-title {
  color: #f5f5f5;
}

.theme--dark .np-subtitle {
  color: #888888;
}

.theme--dark .np-markall-btn {
  background-color: #1a1a1a !important;
  color: #e5e5e5 !important;
  border: 1px solid #2a2a2a !important;
}

.theme--dark .np-filter {
  background-color: #161616;
  color: #888888;
}
.theme--dark .np-filter:hover {
  background-color: #1f1f1f;
}
.theme--dark .np-filter.active {
  background-color: #22d3ee;
  color: #06222b;
}
.theme--dark .np-filter .np-filter-count {
  background-color: #2a2a2a;
  color: #aaaaaa;
}
.theme--dark .np-filter.active .np-filter-count {
  background-color: rgba(0, 0, 0, 0.2);
  color: #06222b;
}

.theme--dark .np-card {
  background-color: #111111;
  border-color: #1f1f1f;
}
.theme--dark .np-card:hover {
  border-color: #2a2a2a;
}
.theme--dark .np-card.unread {
  background-color: rgba(34, 211, 238, 0.04);
  border-color: rgba(34, 211, 238, 0.2);
}

.theme--dark .np-delete ::v-deep .v-icon {
  color: #777777 !important;
}

/* ════════════════════════════════════════════════════════
   LIGHT
   ════════════════════════════════════════════════════════ */
.theme--light .np-title,
.theme--light .np-card-title,
.theme--light .np-state-title {
  color: #1D1D1F;
}

.theme--light .np-subtitle {
  color: #86868B;
}

.theme--light .np-markall-btn {
  background-color: #FFFFFF !important;
  color: #1D1D1F !important;
  border: 1px solid #E5E5E7 !important;
}

.theme--light .np-filter {
  background-color: #EEEEF0;
  color: #6B7280;
}
.theme--light .np-filter:hover {
  background-color: #E5E5E7;
}
.theme--light .np-filter.active {
  background-color: #22d3ee;
  color: #06222b;
}
.theme--light .np-filter .np-filter-count {
  background-color: #D5D5D8;
  color: #555555;
}
.theme--light .np-filter.active .np-filter-count {
  background-color: rgba(0, 0, 0, 0.15);
  color: #06222b;
}

.theme--light .np-card {
  background-color: #FFFFFF;
  border-color: #E5E5E7;
}
.theme--light .np-card:hover {
  border-color: #D0D0D3;
}
.theme--light .np-card.unread {
  background-color: rgba(34, 211, 238, 0.06);
  border-color: rgba(34, 211, 238, 0.3);
}

.theme--light .np-card-msg,
.theme--light .np-state-text {
  color: #6B7280;
}
.theme--light .np-card-time {
  color: #9CA3AF;
}

.theme--light .np-delete ::v-deep .v-icon {
  color: #9CA3AF !important;
}
</style>
